import {
  createContext,
  ForwardedRef,
  forwardRef,
  MouseEvent,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';
import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from '@radix-ui/react-popover';
import { AnimatePresence, motion } from 'motion/react';
import { Link, useLocation } from 'react-router';

import { cn, easeOutSmooth, ShimmerText } from '@components/primitives';

import css from './styles.module.css';

interface DropdownContextType {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const DropdownContext = createContext<DropdownContextType>({
  open: false,
  setOpen: () => {},
});

export interface DropdownSectionProps extends PropsWithChildren {
  label?: ReactNode;
  dimmed?: boolean;
  disabled?: boolean;
}

function DropdownSection({
  children,
  dimmed,
  label,
  disabled,
}: DropdownSectionProps) {
  if (disabled) {
    return null;
  }

  return (
    <div className={cn(css.dropdownSection, dimmed && css.dimmed)}>
      {label && <p className={css.dropdownSectionLabel}>{label}</p>}
      {children}
    </div>
  );
}

export interface DropdownItemProps {
  icon: ReactNode;
  label: ReactNode;
  suffix?: ReactNode;
  to?: string;
  onClick?(event: MouseEvent): void;
  disabled?: boolean;
  variant?: 'default' | 'branded';
  state?: any;
}

const DropdownItem = forwardRef<HTMLElement, DropdownItemProps>(
  function DropdownItem(
    {
      icon,
      label,
      suffix,
      to,
      onClick,
      disabled,
      variant = 'default',
      state,
      ...props
    },
    ref
  ) {
    const location = useLocation();
    const { setOpen } = useContext(DropdownContext);

    const handleClick = useCallback(
      (event: MouseEvent) => {
        onClick?.(event);
        if (!event.defaultPrevented) {
          setOpen(false);
        }
      },
      [onClick, setOpen]
    );

    const inner = (
      <>
        <div className={css.itemIcon} aria-hidden>
          {icon}
        </div>

        {variant === 'branded' ? (
          <ShimmerText className={css.itemLabel}>{label}</ShimmerText>
        ) : (
          <span className={css.itemLabel}>{label}</span>
        )}

        {suffix}
      </>
    );

    if (to) {
      const isExternal = to.startsWith('http');
      return (
        <Link
          ref={ref as ForwardedRef<HTMLAnchorElement>}
          to={to}
          target={isExternal ? '_blank' : undefined}
          state={state ?? { back: location }}
          onClick={handleClick}
          className={css.item}
          data-disabled={disabled}
          data-variant={variant}
          {...props}
        >
          {inner}
        </Link>
      );
    } else if (onClick) {
      return (
        <button
          ref={ref as ForwardedRef<HTMLButtonElement>}
          onClick={handleClick}
          className={css.item}
          disabled={disabled}
          data-variant={variant}
          {...props}
        >
          {inner}
        </button>
      );
    } else {
      return (
        <div
          ref={ref as ForwardedRef<HTMLDivElement>}
          className={css.item}
          data-disabled={disabled}
          data-variant={variant}
          {...props}
        >
          {inner}
        </div>
      );
    }
  }
);

export interface DropdownProps extends PropsWithChildren {
  className?: string;
  trigger: ReactNode;
  align?: 'start' | 'end';
}

function DropdownRoot({
  trigger,
  children,
  align = 'start',
  className,
}: DropdownProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <AnimatePresence>
        {open && (
          <PopoverPortal forceMount>
            <PopoverContent
              asChild
              sideOffset={8}
              align={align}
              collisionPadding={8}
            >
              <motion.div
                className={cn(css.dropdown, className)}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2, ease: easeOutSmooth }}
              >
                <div className={css.dropdownContent}>
                  <DropdownContext.Provider value={{ open, setOpen }}>
                    {children}
                  </DropdownContext.Provider>
                </div>
              </motion.div>
            </PopoverContent>
          </PopoverPortal>
        )}
      </AnimatePresence>
    </Popover>
  );
}

export const NavigationDropdown = Object.assign(DropdownRoot, {
  Section: DropdownSection,
  Item: DropdownItem,
});
